import type { ReponseErreur } from './erreurs';

/**
 * Client HTTP.
 *
 * Le jeton d'accès vit en mémoire, jamais dans `localStorage` : une XSS ne peut
 * pas le lire. Le jeton de rafraîchissement est un cookie httpOnly posé par
 * l'API, que le navigateur renvoie tout seul.
 */
let jetonAcces: string | null = null;
let surExpiration: (() => void) | null = null;

export function poserJeton(jeton: string | null): void {
  jetonAcces = jeton;
}
export function aUnJeton(): boolean {
  return jetonAcces !== null;
}
export function surSessionExpiree(rappel: () => void): void {
  surExpiration = rappel;
}

export class ErreurApi extends Error {
  constructor(
    message: string,
    readonly statut: number,
    readonly details: string[] = [],
  ) {
    super(message);
    this.name = 'ErreurApi';
  }

  /** Vrai quand rien n'a atteint le serveur : coupure réseau, API arrêtée. */
  get estReseau(): boolean {
    return this.statut === 0;
  }
}

async function lireErreur(reponse: Response): Promise<ErreurApi> {
  try {
    const corps = (await reponse.json()) as ReponseErreur;
    return new ErreurApi(corps.message ?? reponse.statusText, reponse.status, corps.details ?? []);
  } catch {
    return new ErreurApi(reponse.statusText || 'Erreur inattendue.', reponse.status);
  }
}

let rafraichissementEnCours: Promise<boolean> | null = null;

/** Tente un renouvellement du jeton. Mutualisé : plusieurs 401 simultanés ne déclenchent qu'un appel. */
async function rafraichir(): Promise<boolean> {
  rafraichissementEnCours ??= (async () => {
    try {
      const r = await fetch('/api/auth/rafraichir', { method: 'POST', credentials: 'include' });
      if (!r.ok) return false;
      const corps = (await r.json()) as { acces: string };
      jetonAcces = corps.acces;
      return true;
    } catch {
      return false;
    } finally {
      // Libéré au tick suivant, le temps que les appels en attente lisent le résultat.
      setTimeout(() => { rafraichissementEnCours = null; }, 0);
    }
  })();
  return rafraichissementEnCours;
}

async function envoyer<T>(chemin: string, options: RequestInit, reessaye = true): Promise<T> {
  let reponse: Response;
  try {
    reponse = await fetch(chemin, {
      ...options,
      credentials: 'include',
      headers: {
        ...(options.body ? { 'content-type': 'application/json' } : {}),
        ...(jetonAcces ? { authorization: `Bearer ${jetonAcces}` } : {}),
        ...options.headers,
      },
    });
  } catch {
    throw new ErreurApi(
      "Impossible de joindre le serveur. Vérifiez votre connexion — vos modifications n'ont pas été enregistrées.",
      0,
    );
  }

  if (reponse.status === 401 && reessaye && !chemin.includes('/auth/')) {
    if (await rafraichir()) return envoyer<T>(chemin, options, false);
    jetonAcces = null;
    surExpiration?.();
  }

  if (!reponse.ok) throw await lireErreur(reponse);
  if (reponse.status === 204) return undefined as T;
  return (await reponse.json()) as T;
}

export const api = {
  get: <T>(chemin: string) => envoyer<T>(chemin, { method: 'GET' }),
  post: <T>(chemin: string, corps?: unknown) =>
    envoyer<T>(chemin, { method: 'POST', body: corps === undefined ? undefined : JSON.stringify(corps) }),
  patch: <T>(chemin: string, corps: unknown) =>
    envoyer<T>(chemin, { method: 'PATCH', body: JSON.stringify(corps) }),
  put: <T>(chemin: string, corps: unknown) =>
    envoyer<T>(chemin, { method: 'PUT', body: JSON.stringify(corps) }),
  delete: <T>(chemin: string) => envoyer<T>(chemin, { method: 'DELETE' }),
};

/** Construit une query string en ignorant les valeurs vides. */
export function requete(params: Record<string, string | number | undefined | null>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}
